import { FC, memo, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../redux/hooks'
import { ListContainer, CardsRow } from './styles'
import RecentSearchCard from './card'
import RecentSearchesSkeleton from './skeleton'
import { getSearchbarHistoryItemsState } from '../../../redux/searchbar/selectors'
import { SearchbarHistoryItem } from '../../../redux/searchbar/types'
import { saveHistoryItem } from '../../../redux/searchbar/actions'
import { useTranslation } from '../../../hooks/locale'
import { format as formatDateFns } from 'date-fns'
import { getSearchDate } from '../../../utils/miscellaneous-utils'
import { useSearchUrl } from '../../../hooks/search'
import { getFilterFromHref } from '../../../utils/request-utils'
import AnalyticsService from '../../../services/analytics-service'
import { cardEventCategories } from '../../../services/analytics-service/categories'
import { Section, TitleContainer, Title, Subtitle } from '../../home/common/styles'
import { useABVariant } from '../../../utils/hooks'
import { useABConfig } from '../../../context/ab-config-context'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'

const WIDGET_ID = 'recent-searches-widget'

// Runs synchronously as the browser parses this markup, before it paints this section — well before
// React hydrates. `historyItems` lives in redux-persist's localStorage (unavailable during SSR), so the
// server and the first client render both default to the skeleton below. If there's nothing to show,
// this hides that reserved space before it's ever painted, instead of collapsing it after the fact
// (which is what caused the CLS this replaces). Keep the "arrival is today or later" filter here in
// sync with `filterItemsByToday` in redux/searchbar/selectors.ts — otherwise a visitor with only
// past-dated history would see the skeleton reserved forever with nothing to fill it.
const NO_FLASH_SCRIPT = `
(function () {
    var el = document.getElementById('${WIDGET_ID}');
    if (!el) return;
    try {
        var raw = localStorage.getItem('persist:root');
        var searchbar = raw && JSON.parse(raw).searchbar;
        var items = searchbar ? JSON.parse(searchbar).historyItems || [] : [];
        var todayMs = new Date().setHours(0, 0, 0, 0);
        var hasUpcoming = items.some(function (item) {
            return !item.arrival || new Date(item.arrival).getTime() >= todayMs;
        });
        if (!hasUpcoming) {
            el.style.display = 'none';
        }
    } catch (e) {
        el.style.display = 'none';
    }
})();
`

const getItemKey = (item: SearchbarHistoryItem): string =>
    `${item.search}|${item.arrival ? formatDateFns(item.arrival, 'ddMMyy') : ''}|${
        item.departure ? formatDateFns(item.departure, 'ddMMyy') : ''
    }|${item.adults}|${item.children}`

interface Props {
    title?: string
    subtitle?: string
    isABGated?: boolean
}

const RecentSearchesWidget: FC<Props> = ({
    title = 'pages.home.recentSearches.heading',
    subtitle = 'pages.home.recentSearches.subheading',
    isABGated = false,
}) => {
    const dispatch = useAppDispatch()
    const liveHistoryItems = useSelector(getSearchbarHistoryItemsState)
    const { t } = useTranslation()
    const { getSearchUrl } = useSearchUrl()

    const { isProvidedVariantActive: isRecentSearchesEnabled } = useABVariant(
        AB_KEY.RECENT_SEARCHES,
        AB_VARIANT_VERSION.ONE,
    )

    const { hasABConfigLoaded, error: abConfigError } = useABConfig()
    const isResolving = isABGated && !hasABConfigLoaded && !abConfigError
    const isEnabled = !isABGated || isRecentSearchesEnabled

    const [loadingItemKey, setLoadingItemKey] = useState<string | null>(null)

    const historyItemsRef = useRef<SearchbarHistoryItem[]>(liveHistoryItems)
    if (historyItemsRef.current.length === 0 && liveHistoryItems.length > 0) {
        historyItemsRef.current = liveHistoryItems
    }
    const historyItems = historyItemsRef.current

    const _handleHistoryItemSelect = useCallback(
        async (item: SearchbarHistoryItem) => {
            dispatch(saveHistoryItem(item))
            setLoadingItemKey(getItemKey(item))

            try {
                const searchUrl = await getSearchUrl(getFilterFromHref(globalThis.location.href), {
                    regionId: null,
                    search: item.search,
                    arrival: getSearchDate(item.arrival),
                    departure: getSearchDate(item.departure),
                    adults: item.adults,
                    children: item.children,
                })

                const fullUrl = `${t('traumBaseUrl')}${searchUrl}`

                try {
                    AnalyticsService.trackClickEvent(cardEventCategories.sections.RECENT_SEARCHES, searchUrl)
                } catch (analyticsError) {
                    console.error('Analytics tracking failed:', analyticsError)
                }
                setLoadingItemKey(null)
                globalThis.location.href = fullUrl
            } catch (error) {
                console.error('Failed to navigate to search results:', error)
                setLoadingItemKey(null)
            }
        },
        [dispatch, getSearchUrl, t],
    )

    if (historyItems.length === 0 || isResolving) {
        return (
            <Section id={WIDGET_ID}>
                <RecentSearchesSkeleton TitleContainer={TitleContainer} />
                <script dangerouslySetInnerHTML={{ __html: NO_FLASH_SCRIPT }} />
            </Section>
        )
    }

    if (!isEnabled) {
        return null
    }

    return (
        <Section id={WIDGET_ID}>
            <TitleContainer>
                <Title>{t(title)}</Title>
                <Subtitle>{t(subtitle)}</Subtitle>
            </TitleContainer>
            <ListContainer>
                <CardsRow>
                    {historyItems.map(item => (
                        <RecentSearchCard
                            key={`recent-searches-${item._time}`}
                            historyItem={item}
                            onSelect={_handleHistoryItemSelect}
                            isLoading={loadingItemKey === getItemKey(item)}
                        />
                    ))}
                </CardsRow>
            </ListContainer>
        </Section>
    )
}

export default memo(RecentSearchesWidget)
